import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Typography from '../../sharedComponents/Typography/Typography';
import TextInput from '../../sharedComponents/TextInput/TextInput';
import {
  AddPayoutAccountUKSortCodeFormProps,
  UKSortCodeBankForm,
} from './AddPayoutAccountUKSortCodeFormProps.types';
import { CountryCode } from '../../utils/types/CountryCode/CountryCode.types';
import { CurrencyUnit } from '../../utils/types/Currency/Currency.types';
import styles from './add-payout-account-uk-sort-code-form.module.scss';
import {
  FORM_ID,
  hasNoEmptyFields,
  LegalType as LT,
  ukSortCodeBankFormValidator,
} from '../../utils/validate';

const AddPayoutAccountUKSortCodeForm: React.FC<
  AddPayoutAccountUKSortCodeFormProps
> = ({ handleFormComplete, handleFormChange }) => {
  const { t } = useTranslation();
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [localForm, setLocalForm] = useState<UKSortCodeBankForm>({
    legalType: LT.PRIVATE,
    accountHolderName: '',
    sortCode: '',
    accountNumber: '',
    countryCode: CountryCode.UK.toString(),
    currency: CurrencyUnit.GBP.toString(),
    city: '',
    addressLine: '',
    postalCode: '',
  });
  const formID = FORM_ID.SORT_CODE;

  const handleFieldChange =
    (field: keyof UKSortCodeBankForm) => (value: string) => {
      setLocalForm((prev) => {
        const nextForm = {
          ...prev,
          [field]: value,
        };

        const formFilledOut = hasNoEmptyFields({ form: nextForm });
        if (!formFilledOut) {
          setFormErrors({});
          handleFormComplete(false);
          return nextForm;
        }

        const result = ukSortCodeBankFormValidator(nextForm);
        if (!result.isValid) {
          setFormErrors(
            result.errors.reduce<Record<string, string>>(
              (
                acc: Record<string, string>,
                { field, message }: Record<string, string>,
              ) => {
                acc[field as string] = message;
                return acc;
              },
              {},
            ),
          );
          handleFormComplete(false);
        } else {
          setFormErrors({});
          handleFormComplete(true);
          handleFormChange({ id: formID, form: nextForm });
        }

        return nextForm;
      });
    };

  return (
    <div className={styles.container}>
      <Typography variant={'h5'}>
        {t('sunshine.label.sort_code_account')}
      </Typography>
      <TextInput
        handleChange={handleFieldChange('accountHolderName')}
        label={t('sunshine.label.account_holder_name')}
        value={localForm.accountHolderName}
        errorMsg={formErrors.accountHolderName ?? ''}
      />
      <TextInput
        handleChange={handleFieldChange('sortCode')}
        label={t('sunshine.label.sort_code')}
        value={localForm.sortCode}
        errorMsg={formErrors.sortCode ?? ''}
      />
      <TextInput
        handleChange={handleFieldChange('accountNumber')}
        label={t('sunshine.label.account_number')}
        value={localForm.accountNumber}
        errorMsg={formErrors.accountNumber ?? ''}
      />
      <TextInput
        handleChange={handleFieldChange('city')}
        label={t('sunshine.label.city')}
        value={localForm.city}
        errorMsg={formErrors.city ?? ''}
      />
      <TextInput
        handleChange={handleFieldChange('addressLine')}
        label={t('sunshine.label.address')}
        value={localForm.addressLine}
        errorMsg={formErrors.addressLine ?? ''}
      />
      <TextInput
        handleChange={handleFieldChange('postalCode')}
        label={t('sunshine.label.postal_code')}
        value={localForm.postalCode}
        errorMsg={formErrors.postalCode ?? ''}
      />
    </div>
  );
};

export default AddPayoutAccountUKSortCodeForm;
